import { Formik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { AiOutlineClose } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from 'yup';
import { ImSpinner9 } from "react-icons/im";
import { deleteUser, sendDeleteOtp, verifyDeleteOtp } from "../Redux/Slice/user.slice";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";

const DeleteAccount = () => {
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const navigate = useNavigate();
  const loading = useSelector((state) => state.user.loading);
  const currentUser = useSelector((state) => state.user.currUser);

  useEffect(() => {
    document.title = 'Delete Account • YOYO';
    window.scrollTo(0, 0);
  }, []);

  const handleDeleteAccount = () => {
    setShowModal(true);
  };

  const cancelDelete = () => {
    setShowModal(false);
    setShowOtpModal(false);
  };

  const length = 4
  const onComplete = (otpValue) => { }

  const inputRefs = useRef([]);
  const dispatch = useDispatch();
  const [otp, setOtp] = useState(new Array(length).fill(""));
  const [error, setError] = useState("");

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < length - 1) {
      inputRefs.current[index + 1].focus();
    }

    const otpValue = newOtp.join("");
    if (otpValue.length === length) {
      onComplete?.(otpValue);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").slice(0, length);

    if (/^\d+$/.test(pastedData)) {
      const newOtp = [...otp];
      pastedData.split("").forEach((digit, index) => {
        if (index < length) {
          newOtp[index] = digit;
        }
      });
      setOtp(newOtp);

      if (pastedData.length === length) {
        onComplete?.(pastedData);
      }
      // Focus last filled input or first empty input
      const focusIndex = Math.min(pastedData.length, length - 1);
      inputRefs.current[focusIndex].focus();
    }
  };

  const handleotpSubmit = async (e) => {
    e.preventDefault();
    const otpValue = otp.join("");
    if (otpValue.length !== length) {
      setError("Please enter the complete OTP.");
      return;
    }
    setError("");
    // console.log(otpValue);
    try {
      const payload = { otp: otpValue, email: email }; // Only include email in the payload
      const response = await dispatch(verifyDeleteOtp(payload));
      if (response.payload.success) {
        dispatch(deleteUser(currentUser._id)).then((response) => {
          navigate("/");
          localStorage.removeItem("yoyouserId");
          localStorage.removeItem("yoyoToken");
        });
      } else {
        setError("OTP verification failed. Please try again.");
      }
    } catch (error) {
      setError("Error verifying OTP. Please try again.");
    }
  };

  return (
    <div className="bg-[#141414] border-[2px] border-white/20 text-white p-4 md:p-6 lg:p-8 rounded-lg relative">
      <h2 className="text-base md:text-xl font-semibold mb-6">
        When you delete your gaming account
      </h2>

      <ul className="space-y-4 mb-[35px]">
        {[
          "Your shopping cart, wishlist, and order history will be permanently deleted.",
          "You will no longer be able to re-download or update any previously purchased games.",
          "All your saved payment methods will be removed.",
          "You will no longer receive game deals, exclusive offers, or store updates.",
          "Your customer profile, reviews, and preferences will not be recoverable.",
        ].map((item, idx) => (
          <li className="flex items-start text-sm md:text-base" key={idx}>
            <span className="w-2 h-2 bg-white rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <span className="text-gray-300">{item}</span>
          </li>
        ))}
      </ul>
      <button
        onClick={handleDeleteAccount}
        className="text-white bg-gradient-primary font-normal py-[6px] md600:py-[10px] px-[6px] md600:px-[10px] rounded-[4px] duration-200 w-full sm:w-[50%] lg:w-[30%] transition-[background-position] duration-400 ease-in-out"

        onMouseEnter={(e) => (e.currentTarget.style.backgroundPosition = 'right center')}
        onMouseLeave={(e) => (e.currentTarget.style.backgroundPosition = 'left center')}
      >
        Delete Account
      </button>

      {/* First Modal */}
      {showModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 text-white w-full max-w-[400px] rounded-[32px] shadow-2xl relative overflow-hidden">
            <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
              <h1 className="text-xl font-bold tracking-tight">Delete Account</h1>
              <button
                onClick={cancelDelete}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>
            <Formik
              initialValues={{
                email: "",
                showPassword: false,
              }}
              validationSchema={Yup.object().shape({
                email: Yup.string()
                  .required("Email is required")
                  .email("Please enter a valid email"),
              })}
              onSubmit={async (values) => {
                // Only handle email
                setEmail(values.email); // Updated to use values.email
                await dispatch(sendDeleteOtp({ email: values.email })).then((response) => {
                  if (response.payload?.success) {
                    setShowModal(false);
                    setShowOtpModal(true);
                  }
                });
              }}
            >
              {({ values, errors, touched, handleSubmit, handleChange, setFieldValue, }) => (
                <form onSubmit={handleSubmit}>
                  <div className="flex flex-col relative gap-4">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Confirm Email</label>
                      <input
                        type="text"
                        placeholder="Enter your email address"
                        name="email"
                        className="w-full bg-white/5 border border-white/10 focus:border-red-500/50 focus:bg-white/[0.08] text-sm text-white px-5 py-4 rounded-2xl outline-none transition-all duration-300"
                        value={values.email}
                        onChange={handleChange}
                      />
                      {errors.email && touched.email && (
                        <div className="text-red-400 text-[10px] font-bold uppercase tracking-wider ml-1">
                          {errors.email}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      <button
                        type="button"
                        onClick={cancelDelete}
                        className="flex-1 order-2 sm:order-1 px-6 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        disabled={loading}
                        type="submit"
                        className="flex-[1.5] order-1 sm:order-2 px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center"
                      >
                        {loading ? (
                          <ImSpinner9 className="animate-spin text-lg" />
                        ) : (
                          'Send OTP'
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              )}
            </Formik>
          </div>
        </div>,
        document.body
      )}

      {/* OTP Modal */}
      {showOtpModal && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[9999] px-4">
          <div className="bg-[#141414] border border-white/10 p-6 sm:p-8 text-white w-full max-w-[400px] rounded-[32px] shadow-2xl relative overflow-hidden">

            {/* Title */}
            <div className="flex justify-between items-center border-b border-white/10 pb-4 mb-6">
              <h2 className="text-xl font-bold tracking-tight">Verify OTP</h2>
              <button
                onClick={cancelDelete}
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
              >
                <AiOutlineClose className="text-xl" />
              </button>
            </div>

            <p className="text-gray-400 text-sm leading-relaxed mb-6 text-center">
              We’ve sent a code to <span className='text-white underline font-semibold'>{email}</span>. Please enter it below.
            </p>
            <form onSubmit={handleotpSubmit}>
              <div className="flex justify-center space-x-3 mb-6">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(ref) => inputRefs.current[index] = ref}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={digit}
                    placeholder='•'
                    onChange={(e) => handleChange(e, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    onPaste={handlePaste}
                    maxLength={1}
                    className="w-12 h-12 bg-white/5 border border-white/10 rounded-xl text-white text-center text-xl font-bold focus:outline-none focus:border-[#6263ED]/50 focus:bg-white/[0.08] transition-all duration-200"
                  />
                ))}
              </div>
              {error && <div className="text-red-400 text-[10px] font-bold uppercase tracking-wider text-center mb-4">{error}</div>}

              <button
                type="submit"
                className="w-full px-6 py-4 rounded-2xl bg-gradient-primary text-white font-bold text-sm shadow-lg shadow-[#6263ED]/20 hover:shadow-[#6263ED]/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
              >
                Delete Account
              </button>
            </form>
            <div className="text-center mt-6">
              <p className='text-gray-500 text-xs font-semibold'>Didn't receive code? <span className='text-white hover:underline cursor-pointer transition-all'>Resend</span></p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div >
  );
};

export default DeleteAccount;
